import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';
import { 
  getNumericPrice, 
  getNumericDuration, 
  getTotalDuration as calculateTotalDuration,
  getTotalPrice as calculateTotalPrice,
  normalizeService
} from '../../utils/bookingUtils';

// LocalStorage key for persisting booking state
const BOOKING_STORAGE_KEY = 'lotus_spa_booking_state';

// Define types for our booking data
export type Service = {
  id: string;
  name: string;
  duration: number | string; // in minutes
  price: number | string;
  category_id: string;
  short_description?: string;
  description?: string;
  numericPrice?: number;
  numericDuration?: number;
  discountedPrice?: string; // Promotional/sale price (if set, use this instead of price)
};

export type StaffMember = {
  id: string;
  name: string;
  role: string;
  photo: string;
};

export type CustomerInfo = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes?: string;
};

export type BookingState = {
  selectedServices: Service[];
  selectedStaff: StaffMember | null;
  selectedDate: Date | null;
  selectedTime: string | null;
  customerInfo: CustomerInfo | null;
  currentStep: number;
  paymentIntentId: string | null;
  paymentStatus: 'pending' | 'processing' | 'succeeded' | 'failed' | null;
  appointmentId: string | null;
};

// Helper functions for localStorage
const saveBookingToStorage = (state: BookingState) => {
  try {
    const stateToSave = {
      ...state,
      selectedDate: state.selectedDate?.toISOString() || null,
    };
    localStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(stateToSave));
  } catch (error) {
    console.error('Failed to save booking to localStorage:', error);
  }
};

const loadBookingFromStorage = (): BookingState | null => {
  try {
    const saved = localStorage.getItem(BOOKING_STORAGE_KEY);
    if (!saved) return null;
    
    const parsed = JSON.parse(saved);
    return {
      ...parsed,
      selectedDate: parsed.selectedDate ? new Date(parsed.selectedDate) : null,
    };
  } catch (error) {
    console.error('Failed to load booking from localStorage:', error);
    return null;
  }
};

const clearBookingFromStorage = () => {
  try {
    localStorage.removeItem(BOOKING_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear booking from localStorage:', error);
  }
};

// Define the context type
type BookingContextType = {
  bookingState: BookingState;
  addService: (service: Service) => void;
  removeService: (serviceId: string) => void;
  setSelectedStaff: (staff: StaffMember | null) => void;
  setSelectedDate: (date: Date | null) => void;
  setSelectedTime: (time: string | null) => void;
  setCustomerInfo: (info: CustomerInfo) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  resetBooking: () => void;
  getTotalDuration: () => number;
  getTotalPrice: () => number;
  setPaymentIntent: (paymentIntentId: string) => void;
  setPaymentStatus: (status: 'pending' | 'processing' | 'succeeded' | 'failed') => void;
  setAppointmentId: (appointmentId: string) => void;
  saveBookingState: () => void;
  clearBookingState: () => void;
};

// Initial state
const initialState: BookingState = {
  selectedServices: [],
  selectedStaff: null,
  selectedDate: null,
  selectedTime: null,
  customerInfo: null,
  currentStep: 1,
  paymentIntentId: null,
  paymentStatus: null,
  appointmentId: null,
};

// Create context with default values
const BookingContext = createContext<BookingContextType>({
  bookingState: initialState,
  addService: () => {},
  removeService: () => {},
  setSelectedStaff: () => {},
  setSelectedDate: () => {},
  setSelectedTime: () => {},
  setCustomerInfo: () => {},
  nextStep: () => {},
  prevStep: () => {},
  goToStep: () => {},
  resetBooking: () => {},
  getTotalDuration: () => 0,
  getTotalPrice: () => 0,
  setPaymentIntent: () => {},
  setPaymentStatus: () => {},
  setAppointmentId: () => {},
  saveBookingState: () => {},
  clearBookingState: () => {}
});

/**
 * Provider component for the booking context
 */
export const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [bookingState, setBookingState] = useState<BookingState>(() => {
    // Try to load saved booking state on initial mount
    const saved = loadBookingFromStorage();
    
    // If saved state is a completed booking, clear it and start fresh
    if (saved && saved.appointmentId && saved.paymentStatus === 'succeeded') {
      clearBookingFromStorage();
      return initialState;
    }

    // Otherwise restore saved state (for incomplete bookings or Afterpay returns)
    if (saved) {
      return saved;
    }
    
    return initialState;
  });

  // Load booking from localStorage on mount (for Afterpay returns)
  useEffect(() => {
    const saved = loadBookingFromStorage();
    
    // Only restore if it's an incomplete booking with appointmentId (Afterpay redirect)
    if (saved && saved.appointmentId && saved.paymentStatus !== 'succeeded') {
      setBookingState(saved);
    }
  }, []);

  const addService = useCallback((service: Service) => {
    const normalizedService = normalizeService(service);
    setBookingState(prev => ({
      ...prev,
      selectedServices: [...prev.selectedServices, normalizedService]
    }));
  }, []);

  const removeService = useCallback((serviceId: string) => {
    setBookingState(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.filter(s => s.id !== serviceId)
    }));
  }, []);

  const setSelectedStaff = useCallback((staff: StaffMember | null) => {
    setBookingState(prev => ({ ...prev, selectedStaff: staff }));
  }, []);

  const setSelectedDate = useCallback((date: Date | null) => {
    setBookingState(prev => ({ ...prev, selectedDate: date }));
  }, []);

  const setSelectedTime = useCallback((time: string | null) => {
    setBookingState(prev => ({ ...prev, selectedTime: time }));
  }, []);

  const setCustomerInfo = useCallback((info: CustomerInfo) => {
    setBookingState(prev => ({ ...prev, customerInfo: info }));
  }, []);

  const nextStep = useCallback(() => {
    setBookingState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  }, []);

  const prevStep = useCallback(() => {
    setBookingState(prev => ({ ...prev, currentStep: Math.max(1, prev.currentStep - 1) }));
  }, []);

  const goToStep = useCallback((step: number) => {
    setBookingState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const resetBooking = useCallback(() => {
    setBookingState(initialState);
    clearBookingFromStorage();
  }, []);

  const saveBookingState = useCallback(() => {
    saveBookingToStorage(bookingState);
  }, [bookingState]);

  const clearBookingState = useCallback(() => {
    clearBookingFromStorage();
  }, []);

  const getTotalDuration = useCallback(() => {
    return calculateTotalDuration(bookingState.selectedServices);
  }, [bookingState.selectedServices]);

  const getTotalPrice = useCallback(() => {
    return calculateTotalPrice(bookingState.selectedServices);
  }, [bookingState.selectedServices]);

  const setPaymentIntent = useCallback((paymentIntentId: string) => {
  setBookingState(prev => ({ ...prev, paymentIntentId }));
}, []);

const setPaymentStatus = useCallback((status: 'pending' | 'processing' | 'succeeded' | 'failed') => {
  setBookingState(prev => ({ ...prev, paymentStatus: status }));
}, []);

const setAppointmentId = useCallback((appointmentId: string) => {
  setBookingState(prev => ({ ...prev, appointmentId }));
}, []);

const contextValue = {
  bookingState,
  addService,
  removeService,
  setSelectedStaff,
  setSelectedDate,
  setSelectedTime,
  setCustomerInfo,
  nextStep,
  prevStep,
  goToStep,
  resetBooking,
  getTotalDuration,
  getTotalPrice,
  setPaymentIntent,
  setPaymentStatus,
  setAppointmentId,
  saveBookingState,
  clearBookingState
};

  return (
    <BookingContext.Provider value={contextValue}>
      {children}
    </BookingContext.Provider>
  );
};

// Custom hook to use the booking context
export const useBooking = () => useContext(BookingContext);

export default BookingContext;
